import type { PayslipLineItem } from './types';

/**
 * Single source of truth for payslip line-item classification and totals.
 *
 * Product rule: `benefits` are employer-provided value and are NOT subtracted
 * from the employee's net pay. Only `deductions` and `taxes` reduce net pay.
 */
export type LineItemBucket = 'earnings' | 'deductions' | 'taxes' | 'benefits' | 'other';

export const DEDUCTION_BUCKETS: LineItemBucket[] = ['deductions', 'taxes'];

export function classifyLineItem(item: Pick<PayslipLineItem, 'category'>): LineItemBucket {
  switch ((item.category || '').toLowerCase()) {
    case 'earnings':
      return 'earnings';
    case 'deductions':
      return 'deductions';
    case 'taxes':
      return 'taxes';
    case 'benefits':
      return 'benefits';
    default:
      return 'other';
  }
}

export function isDeductionLineItem(item: Pick<PayslipLineItem, 'category'>): boolean {
  return DEDUCTION_BUCKETS.includes(classifyLineItem(item));
}

export function isEarningLineItem(item: Pick<PayslipLineItem, 'category'>): boolean {
  return classifyLineItem(item) === 'earnings';
}

export function lineItemAmount(item: PayslipLineItem): number {
  return Number(item.gross_amount ?? item.base_amount ?? 0);
}

export interface DerivedPayslipTotals {
  gross_earnings: number;
  total_deductions: number;
  benefits_total: number;
  net_pay: number;
  tax_breakdown: Record<string, number>;
}

export function computeDerivedTotals(lineItems: PayslipLineItem[]): DerivedPayslipTotals {
  const gross_earnings = lineItems
    .filter(isEarningLineItem)
    .reduce((sum, item) => sum + lineItemAmount(item), 0);

  const total_deductions = lineItems
    .filter(isDeductionLineItem)
    .reduce((sum, item) => sum + lineItemAmount(item), 0);

  const benefits_total = lineItems
    .filter((item) => classifyLineItem(item) === 'benefits')
    .reduce((sum, item) => sum + lineItemAmount(item), 0);

  const tax_breakdown = lineItems
    .filter((item) => classifyLineItem(item) === 'taxes')
    .reduce((acc, item) => {
      const key = item.description
        ? item.description.replace(/\s+/g, '_').toLowerCase()
        : `tax_${item.compensation_rule_id}`;
      acc[key] = (acc[key] || 0) + lineItemAmount(item);
      return acc;
    }, {} as Record<string, number>);

  return {
    gross_earnings,
    total_deductions,
    benefits_total,
    net_pay: gross_earnings - total_deductions,
    tax_breakdown,
  };
}

/**
 * Stored engine values are authoritative when present; derived values are only
 * a fallback (legacy payslips) and drive the breakdown sections.
 */
export function resolveDisplayTotals(
  stored: { gross_pay?: number | string | null; net_pay?: number | string | null; total_deductions?: number | string | null },
  derived: DerivedPayslipTotals
) {
  const num = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));
  return {
    gross_earnings: num(stored.gross_pay) ?? derived.gross_earnings,
    total_deductions: num(stored.total_deductions) ?? derived.total_deductions,
    net_pay: num(stored.net_pay) ?? derived.net_pay,
    benefits_total: derived.benefits_total,
    tax_breakdown: derived.tax_breakdown,
  };
}
